import { http } from './request';
import { ACCOUNT_COMPUTE_URLS, ACCOUNT_PAYMENT_URLS } from './urls';

export type AccountPaymentProductType = 'SUBSCRIPTION' | 'COMPUTE_POWER';

export interface AccountPaymentProduct {
  id?: number;
  productType: AccountPaymentProductType | string;
  code: string;
  productCode?: string;
  name: string;
  productName?: string;
  description?: string;
  amountTotal: number;
  amountYuan: string;
  durationDays?: number;
  billingMonths?: number;
  billingPeriod?: string;
  planTierCode?: string;
  planTierName?: string;
  level?: number;
  shopLimit?: number;
  computePowerAmount?: number;
  monthlyComputePowerAmount?: number;
  totalComputePowerAmount?: number;
  featureTags?: string[];
  limits?: Record<string, unknown>;
  extraConfig?: Record<string, unknown>;
  upgradeDetail?: AccountPaymentUpgradeDetail;
}

export type AccountPaymentPlan = AccountPaymentProduct;

export interface AccountPaymentEntitlement {
  active: boolean;
  planCode: string;
  planName: string;
  planTierCode?: string;
  planTierName?: string;
  status?: string;
  purchaseType?: string;
  sourcePaymentOrderNo?: string;
  shopLimit?: number;
  featureTags?: string[];
  limits?: Record<string, unknown>;
  effectiveFrom?: string | null;
  effectiveUntil?: string | null;
}

export interface AccountPaymentPlanTierFeatureConfig {
  planTierCode: string;
  displayName: string;
  level: number;
  featureTags?: string[];
}

export interface AccountPaymentUpgradeDetail {
  currentSubscriptionId?: number;
  currentPlanCode?: string;
  currentPlanName?: string;
  currentPlanTierCode?: string;
  currentPlanTierName?: string;
  targetPlanCode?: string;
  targetPlanName?: string;
  targetPlanTierCode?: string;
  targetPlanTierName?: string;
  remainingDays?: number;
  upgradeMonths?: number;
  upgradeDays?: number;
  residualDays?: number;
  currentAmountTotal?: number;
  targetAmountTotal?: number;
  amountTotal?: number;
  creditAmountTotal?: number;
  currentMonthlyComputePowerAmount?: number;
  targetMonthlyComputePowerAmount?: number;
  computePowerAmount?: number;
  rule?: string;
}

export interface AccountComputeBalance {
  userId: string;
  phone?: string;
  balance: number;
  totalRecharged: number;
  totalConsumed: number;
  updatedAt?: string | null;
}

export interface AccountComputeRecord {
  id: number;
  recordNo: string;
  eventType: string;
  scenarioCode?: string;
  sourceId?: string;
  paymentOrderNo?: string;
  modelProvider?: string;
  modelName?: string;
  costYuan?: string;
  computeDelta: number;
  balanceAfter: number;
  chargeMode?: string;
  remark?: string;
  displayRemark?: string;
  displayStatus?: string;
  displayStatusText?: string;
  createdAt?: string | null;
}

export interface AccountComputeChargeScenario {
  id: number;
  taskType?: string | null;
  scenarioCode: string;
  scenarioName: string;
  chargeType: 'FIXED' | 'PER_UNIT' | string;
  unitName?: string | null;
  unitAmount: number;
  remark?: string | null;
  enabled?: boolean;
  hidden?: boolean;
  hiddenMinBuildVersion?: number | null;
  hiddenMaxBuildVersion?: number | null;
  sortOrder?: number;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface AccountPageResult<T> {
  total: number;
  list: T[];
}

export interface AccountPaymentOrder {
  paymentOrderNo: string;
  outTradeNo?: string;
  userId?: string;
  planCode?: string;
  planName: string;
  productType: string;
  purchaseType?: string;
  amountTotal?: number;
  amountYuan: string;
  creditAmountYuan?: string;
  computePowerAmount?: number;
  payProvider?: string;
  payScene?: string;
  mock?: boolean;
  status: string;
  transactionId?: string;
  createdAt?: string | null;
  paidAt?: string | null;
  records?: unknown[];
  refunds?: unknown[];
  entitlement?: Partial<AccountPaymentEntitlement> | null;
}

export interface AccountPaySession {
  paySessionToken: string;
  paymentOrderNo?: string;
  userId?: string;
  phone?: string;
  planCode: string;
  planName: string;
  productType: string;
  purchaseType: string;
  originalAmountTotal?: number;
  creditAmountTotal?: number;
  amountTotal?: number;
  durationDays?: number;
  originalAmountYuan: string;
  creditAmountYuan: string;
  amountYuan: string;
  currency?: string;
  status?: string;
  expireAt?: string | null;
  wechatOpenidBound?: boolean;
  plan?: AccountPaymentProduct;
  computePowerAmount?: number;
}

export type AccountPaymentSession = AccountPaySession;

export interface CreatePaySessionPayload {
  planCode: string;
  productType?: AccountPaymentProductType;
  purchaseType?: 'NEW' | 'RENEW' | 'UPGRADE' | string;
  expectedAmountTotal?: number;
}

export function listAccountPaymentPlans() {
  return http.get<AccountPaymentProduct[]>(ACCOUNT_PAYMENT_URLS.visiblePlans);
}

export function listAccountComputePackages() {
  return http.get<AccountPaymentProduct[]>(ACCOUNT_PAYMENT_URLS.visibleComputePackages);
}

export function getAccountPaymentEntitlement() {
  return http.get<AccountPaymentEntitlement>(ACCOUNT_PAYMENT_URLS.entitlement);
}

export function listAccountPaymentPlanTiers() {
  return http.get<{ list?: AccountPaymentPlanTierFeatureConfig[] } | AccountPaymentPlanTierFeatureConfig[]>(ACCOUNT_PAYMENT_URLS.planTiers);
}

export function createAccountPaySession(data: CreatePaySessionPayload) {
  return http.post<AccountPaySession>(ACCOUNT_PAYMENT_URLS.paySessions, data);
}

export function getAccountPaySession(paySessionToken: string) {
  return http.get<AccountPaySession>(ACCOUNT_PAYMENT_URLS.paySession(paySessionToken));
}

export function listAccountPaymentOrders(params?: { page?: number; size?: number }) {
  return http.get<AccountPageResult<AccountPaymentOrder>>(ACCOUNT_PAYMENT_URLS.orders, { params });
}

export function getAccountPaymentOrder(paymentOrderNo: string) {
  return http.get<AccountPaymentOrder>(ACCOUNT_PAYMENT_URLS.order(paymentOrderNo));
}

export function getAccountComputeBalance() {
  return http.get<AccountComputeBalance>(ACCOUNT_COMPUTE_URLS.balance);
}

export function listAccountComputeRecords(params?: { page?: number; size?: number }) {
  return http.get<AccountPageResult<AccountComputeRecord>>(ACCOUNT_COMPUTE_URLS.records, { params });
}

export function listAccountComputeChargeScenarios() {
  return http.get<AccountComputeChargeScenario[]>(ACCOUNT_COMPUTE_URLS.chargeScenarios);
}

export const fetchAccountPaymentPlans = listAccountPaymentPlans;
export const fetchAccountPaymentEntitlement = getAccountPaymentEntitlement;
